/**
 * User Service
 * Handles all user-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  User,
  UserListParams,
  UpdateUserRequest,
  PaginatedResponse,
} from '../types';

class UserService {
  /**
   * Get all users with pagination
   */
  async getUsers(params?: UserListParams): Promise<PaginatedResponse<User>> {
    const response = await apiClient.get<PaginatedResponse<User>>(
      API_ENDPOINTS.USERS.BASE,
      { params }
    );
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<User>(
      API_ENDPOINTS.USERS.BY_ID(id)
    );
    return response.data;
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    const response = await apiClient.get<User>(
      API_ENDPOINTS.USERS.PROFILE
    );
    return response.data;
  }

  /**
   * Update user
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<User>(
      API_ENDPOINTS.USERS.BY_ID(id),
      data
    );
    return response.data;
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<User>(
      API_ENDPOINTS.USERS.UPDATE,
      data
    );
    return response.data;
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.USERS.DELETE(id));
  }

  /**
   * Create new user
   */
  async createUser(data: Partial<User>): Promise<User> {
    const response = await apiClient.post<User>(
      API_ENDPOINTS.USERS.BASE,
      data
    );
    return response.data;
  }

  /**
   * Search users
   */
  async searchUsers(query: string, params?: UserListParams): Promise<PaginatedResponse<User>> {
    const response = await apiClient.get<PaginatedResponse<User>>(
      API_ENDPOINTS.USERS.BASE,
      { 
        params: { 
          ...params, 
          search: query 
        } 
      }
    );
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    newUsersToday: number;
    newUsersThisWeek: number;
  }> {
    const response = await apiClient.get<{
      totalUsers: number;
      activeUsers: number;
      newUsersToday: number;
      newUsersThisWeek: number;
    }>(`${API_ENDPOINTS.USERS.BASE}/stats`);
    return response.data;
  }

  /**
   * Export users data
   */
  async exportUsers(params?: UserListParams): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.USERS.BASE}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const userService = new UserService();
export default userService;

